/**
 * GroupSearch — Typeahead search for Dynamics Account groups.
 *
 * Searches by name (2+ characters), returns Group and Prospect Group accounts.
 * Displays name, account number, city/state, and customer type.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import {
  Input,
  makeStyles,
} from '@fluentui/react-components';
import { SearchRegular, DismissRegular } from '@fluentui/react-icons';
import { useGroupSearch, type Account } from '@entities/account';
import { COLORS } from '../tokens';

const useStyles = makeStyles({
  container: {
    position: 'relative',
    width: '100%',
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    left: '0',
    right: '0',
    zIndex: 1000,
    maxHeight: '240px',
    overflow: 'auto',
    backgroundColor: COLORS.white,
    border: `1px solid ${COLORS.border}`,
    borderRadius: '4px',
    marginTop: '2px',
  },
  option: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 12px',
    cursor: 'pointer',
    fontSize: '14px',
    ':hover': {
      backgroundColor: COLORS.bgSurface,
    },
  },
  optionSelected: {
    backgroundColor: COLORS.bgSurface,
  },
  optionName: {
    fontWeight: '500',
    color: COLORS.textPrimary,
  },
  optionMeta: {
    fontSize: '12px',
    color: COLORS.textSecondary,
  },
  noResults: {
    padding: '12px',
    fontSize: '13px',
    color: COLORS.textMuted,
    textAlign: 'center',
  },
  selectedValue: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 12px',
    backgroundColor: COLORS.bgSurface,
    border: `1px solid ${COLORS.border}`,
    borderRadius: '4px',
    fontSize: '14px',
  },
  selectedName: {
    flex: 1,
    fontWeight: '500',
    color: COLORS.textPrimary,
  },
  clearBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    color: COLORS.textMuted,
    padding: 0,
    ':hover': {
      color: COLORS.textPrimary,
      backgroundColor: COLORS.bgBase,
    },
  },
});

interface GroupSearchProps {
  /** Called when a group is selected or cleared */
  onSelect: (account: Account | null) => void;
  /** Currently selected account (controlled) */
  selectedAccount?: Account | null;
  /** Placeholder text */
  placeholder?: string;
}

export function GroupSearch({
  onSelect,
  selectedAccount,
  placeholder = 'Search groups by name...',
}: GroupSearchProps) {
  const styles = useStyles();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: results, isLoading } = useGroupSearch(query);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSelect = useCallback((account: Account) => {
    onSelect(account);
    setQuery('');
    setOpen(false);
  }, [onSelect]);

  const handleClear = useCallback(() => {
    onSelect(null);
    setQuery('');
  }, [onSelect]);

  // Show selected state
  if (selectedAccount) {
    return (
      <div className={styles.selectedValue}>
        <span className={styles.selectedName}>{selectedAccount.name}</span>
        <span style={{ fontSize: '12px', color: COLORS.textMuted, fontFamily: "'JetBrains Mono', monospace" }}>
          {selectedAccount.id.substring(0, 8)}...
        </span>
        <button className={styles.clearBtn} onClick={handleClear} type="button">
          <DismissRegular style={{ fontSize: '14px' }} />
        </button>
      </div>
    );
  }

  return (
    <div className={styles.container} ref={containerRef}>
      <Input
        contentBefore={<SearchRegular />}
        value={query}
        onChange={(_, d) => {
          setQuery(d.value);
          setOpen(d.value.length >= 2);
        }}
        onFocus={() => query.length >= 2 && setOpen(true)}
        placeholder={placeholder}
        style={{ width: '100%' }}
      />

      {open && (
        <div className={styles.dropdown}>
          {isLoading && (
            <div className={styles.noResults}>Searching...</div>
          )}

          {!isLoading && results && results.length === 0 && (
            <div className={styles.noResults}>No groups found</div>
          )}

          {!isLoading && results?.map((account) => (
            <div
              key={account.id}
              className={styles.option}
              onClick={() => handleSelect(account)}
            >
              <div>
                <div className={styles.optionName}>{account.name}</div>
                <div className={styles.optionMeta}>
                  {[
                    account.accountNumber,
                    account.customerTypeName,
                    [account.city, account.state].filter(Boolean).join(', '),
                  ].filter(Boolean).join(' \u00b7 ')}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
